import { Plus } from "lucide-react";
import { useContext, useMemo, useState } from "react";
import MainSectionSection from "../../components/MainSectionSection";
import { trpc } from "../../trpc";
import MerchantContext from "../../contexts/MerchantContext";
import CreateSendModal from "../../components/broadcast/CreateSendModal";
import SendMetricsModal from "../../components/broadcast/SendMetricsModal";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";
import { toBroadcastSendItem, type UnifiedSendItem } from "../../components/broadcast/types";

const BroadcastContainer = (): JSX.Element => {
  const { merchantId } = useContext(MerchantContext);
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<UnifiedSendItem | null>(null);
  const isMobile = useIsMobile();

  const {
    data: broadcasts,
    isLoading,
    refetch: refetchBroadcasts,
  } = trpc.broadcast.list.useQuery({ merchantId: merchantId || "" }, { enabled: !!merchantId });

  const unifiedItems: UnifiedSendItem[] = useMemo(() => {
    return (broadcasts ?? [])
      .map(toBroadcastSendItem)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [broadcasts]);

  const handleSuccess = () => {
    refetchBroadcasts();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status: UnifiedSendItem["status"]) => {
    const statusConfig = {
      pending: { label: "Pendiente", className: "bg-gray-100 text-gray-800 border-gray-200" },
      processing: { label: "Procesando", className: "bg-blue-100 text-blue-800 border-blue-200" },
      completed: { label: "Completado", className: "bg-green-100 text-green-800 border-green-200" },
      failed: { label: "Fallido", className: "bg-red-100 text-red-800 border-red-200" },
    };
    const config = statusConfig[status];
    return (
      <Badge variant="outline" className={cn("text-sm px-2 py-1", config.className)}>
        {config.label}
      </Badge>
    );
  };

  const renderProgressBar = (item: UnifiedSendItem) => {
    if (item.status !== "processing") return null;
    return (
      <div className="mt-1">
        <div className="h-1 w-20 rounded-full bg-gray-200">
          <div
            className="h-1 rounded-full bg-blue-500 transition-all"
            style={{ width: `${item.progressPercentage}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <MainSectionSection gap="24px">
      <CreateSendModal isOpen={isOpen} onClose={() => setIsOpen(false)} onSuccess={handleSuccess} />
      <SendMetricsModal item={selected} onClose={() => setSelected(null)} />

      <div>
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-0">
          <h2 className="text-xl font-bold md:text-2xl">Envío Masivo</h2>
          <Button onClick={() => setIsOpen(true)} className="w-full min-h-11 md:w-auto">
            <Plus className="size-4" />
            Crear Envío Masivo
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-4">
              <Spinner size="xl" />
              <p className="text-gray-600">Cargando envíos masivos...</p>
            </div>
          </div>
        ) : unifiedItems.length > 0 ? (
          <Card>
            <CardContent className="p-0">
              {isMobile ? (
                <div className="flex flex-col">
                  {unifiedItems.map((item, index) => (
                    <div
                      key={item.id}
                      onClick={() => setSelected(item)}
                      className={cn(
                        "cursor-pointer p-3",
                        index < unifiedItems.length - 1 && "border-b border-gray-200"
                      )}
                    >
                      <div className="mb-1 flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <p className="truncate text-sm font-medium">{item.name}</p>
                        </div>
                        {getStatusBadge(item.status)}
                      </div>
                      <div className="mb-1 flex flex-col items-start gap-1">
                        <p className="text-xs text-gray-600">
                          {item.totalRecipients} destinatarios
                        </p>
                        <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                          <span>
                            <span className="font-medium text-blue-500">{item.sentCount}</span> env.
                          </span>
                          <span>
                            <span className="font-medium text-red-500">{item.failedCount}</span> fall.
                          </span>
                          {item.sentCount > 0 && (
                            <>
                              <span>
                                <span className="font-medium text-green-500">{item.deliveredCount}</span> entreg.
                              </span>
                              <span>
                                <span className="font-medium text-purple-500">{item.respondedCount}</span> resp.
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500">{item.createdBy}</p>
                        <p className="text-xs text-gray-500">{formatDate(item.createdAt)}</p>
                      </div>
                      {item.status === "processing" && (
                        <div className="mt-2">
                          <div className="h-1.5 w-full rounded-full bg-gray-200">
                            <div
                              className="h-1.5 rounded-full bg-blue-500 transition-all"
                              style={{ width: `${item.progressPercentage}%` }}
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-400">{item.progressPercentage}%</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Nombre</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Estado</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Destinatarios</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Creado por</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unifiedItems.map((item) => (
                        <tr
                          key={item.id}
                          onClick={() => setSelected(item)}
                          className="cursor-pointer transition-colors duration-200 hover:bg-gray-50"
                        >
                          <td className="px-4 py-3 font-medium">{item.name}</td>
                          <td className="px-4 py-3">
                            {getStatusBadge(item.status)}
                            {renderProgressBar(item)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col items-start gap-1">
                              <p className="text-sm font-medium">{item.totalRecipients} total</p>
                              <div className="flex gap-3 text-xs text-gray-500">
                                <span>
                                  <span className="font-medium text-blue-500">{item.sentCount}</span> enviados
                                </span>
                                <span>
                                  <span className="font-medium text-red-500">{item.failedCount}</span> fallidos
                                </span>
                                {item.skippedOrNotSentCount > 0 && (
                                  <span>
                                    <span className="font-medium text-orange-500">{item.skippedOrNotSentCount}</span>
                                    {" "}existentes
                                  </span>
                                )}
                              </div>
                              {item.sentCount > 0 && (
                                <div className="flex gap-3 pl-3 text-xs text-gray-400">
                                  <span>
                                    ↳ <span className="font-medium text-green-500">{item.deliveredCount}</span> entregados
                                  </span>
                                  <span>
                                    <span className="font-medium text-purple-500">{item.respondedCount}</span> respondidos
                                  </span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{item.createdBy}</td>
                          <td className="px-4 py-3 text-gray-600">{formatDate(item.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col items-center justify-center py-12">
                <p className="mb-4 text-lg text-gray-600">
                  No hay envíos masivos aún
                </p>
                <p className="mb-6 text-sm text-gray-500">
                  Crea tu primer envío masivo para comunicarte con múltiples leads a la vez
                </p>
                <Button onClick={() => setIsOpen(true)}>
                  <Plus className="size-4" />
                  Crear Envío Masivo
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </MainSectionSection>
  );
};

export default BroadcastContainer;
